/**
 * Two-card bento below the map: "Rent/Lease" routes into the Rental Finder;
 * "Hire a Property Manager" routes into `/hire-a-property-manager`
 * (HirePropertyManager.tsx), same destination as the main nav link of the
 * same name. Heading font is --public-hero-display, the same Google Sans
 * pairing the hero headline uses, per earlier direct request to match it
 * rather than the site's default --public-display.
 *
 * Each card gets its own background photo, per direct request — free-to-use
 * stock photography (Pexels license, no attribution required), chosen
 * deliberately with no people in frame: a real (not rendered/AI) brick
 * apartment complex for "Rent/Lease", a real single-family home for "Hire a
 * Property Manager." The photo is passed as a `--bento-photo` custom
 * property rather than duplicated inline `background-image` CSS, so the
 * overlay/border layering stays defined once in styles.css and each card
 * only supplies the one thing that differs.
 *
 * A third, full-width phone CTA card (the exact one from
 * HirePropertyManager.tsx) used to sit below these two — removed per an
 * earlier direct request. The phone CTA still lives on
 * `/hire-a-property-manager` itself, unaffected.
 */

import { Briefcase, KeyRound } from 'lucide-react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import bentoPropertyManager from '../../assets/bento_property_manager.jpg';
import bentoRentLease from '../../assets/bento_rent_lease.jpg';

function bentoPhoto(url: string): CSSProperties {
  return { '--bento-photo': `url(${url})` } as CSSProperties;
}

export function HomeBento() {
  return (
    <section className="public__bento" aria-labelledby="public-bento-heading">
      <h2 id="public-bento-heading" className="visually-hidden">
        What are you looking to do?
      </h2>
      <Link to="/listings" className="public__bento-card" style={bentoPhoto(bentoRentLease)}>
        <KeyRound className="public__bento-icon" aria-hidden="true" />
        <h3>Rent/Lease</h3>
        <p>Browse open units and find your next home.</p>
      </Link>
      <Link
        to="/hire-a-property-manager"
        className="public__bento-card"
        style={bentoPhoto(bentoPropertyManager)}
      >
        <Briefcase className="public__bento-icon" aria-hidden="true" />
        <h3>Hire a Property Manager</h3>
        <p>Let us manage your rental property.</p>
      </Link>
    </section>
  );
}
